import React, {useContext, useMemo, ReactElement} from "react";
import {inject, observer} from "mobx-react";
import { RootStore } from 'stores';
import _ from "lodash";
import {
    AreaField,
    getOptions,
    IntegrationSftpVendorType,
    isTabletPortraitDown,
    SelectField,
    ToastContext,
    Variables
} from "@enter/core";
import * as S from "./shared.styles";
import * as SP from "./settings-page.styles";
import {Icon} from "@mdi/react";
import {mdiArrowLeft} from "@mdi/js";
import styled from "@emotion/styled/macro";

import {Formik, FormikHelpers, FormikProps} from "formik";
import * as Yup from "yup";
import {
    ErrorWarningLabel,
    SideBySideInputGroup,
    SideBySideValue,
    ZeroPadFormField
} from "./settings-page.styles";

const InfoBox = styled.div`
    background-color: ${Variables.Colors.nx4};
    border: 1px solid ${Variables.Colors.n15};
    border-radius: ${Variables.Rounded.medium};
    padding: 16px 20px;
    margin: 24px 0;
`;

const InfoTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${Variables.Colors.n100};
    margin-bottom: 12px;
`;

const InfoList = styled.ul`
    margin: 0;
    padding-left: 20px;
    list-style-type: disc;
`;

const InfoListItem = styled.li`
    font-size: 14px;
    line-height: 1.6;
    color: ${Variables.Colors.n70};
    margin-bottom: 8px;
    
    &:last-child {
        margin-bottom: 0;
    }
    
    i {
        font-family: 'Courier New', monospace;
        background-color: ${Variables.Colors.white};
        padding: 2px 6px;
        border-radius: ${Variables.Rounded.small};
        font-style: normal;
        font-size: 13px;
    }
`;

const FieldDivider = styled.div`
    height: 1px;
    background-color: ${Variables.Colors.platinum};
    margin: 0;
`;

const StyledSideBySideInputGroup = styled(SP.SideBySideInputGroup)`
    padding: 8px 0;
    margin: 0;
    
    ${S.Label} {
        margin-top: 0;
        margin-bottom: 0;
    }
    
    &:first-of-type {
        padding-top: 0;
    }
`;

const StyledSideBySideValue = styled(SP.SideBySideValue)`
    margin-top: 0;
    margin-bottom: 0;
`;

const StyledInputGroup = styled(S.InputGroup)`
    padding: 16px 0 8px 0;
    margin: 0;
    
    ${S.Label} {
        margin-top: 0;
        margin-bottom: 8px;
    }
`;

const StyledSPInputGroup = styled(SP.InputGroup)`
    padding: 8px 0 0 0;
    margin: 0;
    
    ${SP.Label} {
        margin-top: 0;
        margin-bottom: 8px;
    }
`;

const ActionsContainer = styled.div`
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid ${Variables.Colors.platinum};
`;

const StyledErrorWarningLabel = styled(ErrorWarningLabel)`
    margin-bottom: 24px;
    display: block;
`;

export const SftpSettings: React.FunctionComponent<{}> = inject('rootStore')(
    observer(
        (props: {}): React.ReactElement => {
            const [errorState, setErrorState] = React.useState<string|undefined>();
            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const toastContext = useContext(ToastContext);

            const { settingsStore } = rootStore;

            const onFormSubmit = async (values: FormProps, formik: FormikHelpers<FormProps>) => {
                formik.setSubmitting(true);
                setErrorState(undefined);
                try {
                    const previousId: string|undefined = settingsStore.integrationSftp ? settingsStore.integrationSftp.id : undefined;
                    await settingsStore.saveSftpIntegration(values.vendorType, values.publicKey, previousId);
                } catch(e) {
                    if(e instanceof Response) {
                        const resJson = await e.json();
                        setErrorState(resJson.message + " (trace id: " + resJson.trace_id + ")");
                        console.log(resJson);
                    }
                    throw e;
                } finally {
                    formik.setSubmitting(false);
                }
            };

            type FormProps = {
                publicKey: string;
                vendorType: IntegrationSftpVendorType;
            }

            const SftpSchema = Yup.object().shape({
                publicKey: Yup.string().required('Public key is required'),
                vendorType: Yup.mixed<IntegrationSftpVendorType>().oneOf(Object.values(IntegrationSftpVendorType))
            });

            const initialValues: FormProps = useMemo(() => {
                return {
                    publicKey: settingsStore.integrationSftp && settingsStore.integrationSftp.publicKey ? settingsStore.integrationSftp.publicKey : 'ssh-rsa AAAAAAAAAAAAAAAAAAAAAAAAEXAMPLE= user@example.com',
                    vendorType: settingsStore.integrationSftp && settingsStore.integrationSftp.vendorType ? settingsStore.integrationSftp.vendorType :  IntegrationSftpVendorType.ECW
                };
            }, []);


            function sftpForm() : ReactElement {
                return  <Formik initialValues={initialValues} validationSchema={SftpSchema} onSubmit={onFormSubmit}>
                    {(formik: FormikProps<FormProps>) => (
                        <SP.DialogBody>
                            {errorState && (
                                <StyledErrorWarningLabel>
                                    {errorState}
                                </StyledErrorWarningLabel>
                            )}
                            
                            <InfoBox>
                                <InfoTitle>How to Generate Your SSH Key</InfoTitle>
                                <InfoList>
                                    <InfoListItem>
                                        <strong>Windows:</strong> From command prompt, run <i>ssh-keygen -t rsa -b 2048 -C "your_email@example.com"</i> and copy the contents from <i>C:\Users\YOU\.ssh\id_rsa.pub</i>
                                    </InfoListItem>
                                    <InfoListItem>
                                        <strong>Mac/Linux:</strong> Run <i>ssh-keygen -t rsa -b 2048 -C "your_email@example.com"</i> and copy the contents from <i>/Users/YOU/.ssh/id_rsa.pub</i>
                                    </InfoListItem>
                                </InfoList>
                            </InfoBox>

                            <StyledSideBySideInputGroup>
                                <S.Label>Host: </S.Label>
                                <StyledSideBySideValue>sftp.enter.health</StyledSideBySideValue>
                            </StyledSideBySideInputGroup>

                            <StyledSideBySideInputGroup>
                                <S.Label>Username: </S.Label>
                                <StyledSideBySideValue>{settingsStore.integrationSftp ? settingsStore.integrationSftp.username : 'N/A'}</StyledSideBySideValue>
                            </StyledSideBySideInputGroup>

                            <FieldDivider />

                            <StyledInputGroup>
                                <S.Label>Vendor Type</S.Label>
                                <SP.ZeroPadFormField
                                    name="vendorType"
                                    component={SelectField}
                                    options={getOptions(IntegrationSftpVendorType)}
                                    required={true}
                                />
                            </StyledInputGroup>

                            <StyledSPInputGroup>
                                <SP.Label>Public Key</SP.Label>
                                <SP.FormField name="publicKey" component={AreaField} />
                            </StyledSPInputGroup>

                            <ActionsContainer>
                                <S.SubCategoryAddButton variant="primary" type="submit" onClick={formik.submitForm} disabled={formik.isSubmitting}>
                                    {formik.isSubmitting ? <SP.Pending size="20px" /> : <>Save SFTP Configuration</>}
                                </S.SubCategoryAddButton>
                            </ActionsContainer>
                        </SP.DialogBody>
                    )}
                </Formik>
            }


            return <S.SubCategoryCol>
                <S.SubCategoryItem key="members">
                    <S.SubCategoryItemRow>
                        <S.SubCategoryH1>
                            {isTabletPortraitDown() && (
                                <S.ReturnLink to="/settings">
                                    {(): React.ReactElement => <Icon path={mdiArrowLeft} size={1} />}
                                </S.ReturnLink>
                            )}
                            SFTP Configuration
                        </S.SubCategoryH1>

                        {sftpForm()}

                    </S.SubCategoryItemRow>
                </S.SubCategoryItem>
            </S.SubCategoryCol>;
        },
    ),
);